import DialGraphComponent from './DialGraphComponentClassic';
import React, { useMemo } from 'react';
import { Flex, Spinner } from '@patternfly/react-core';
import { useTranslation } from '../../../../localization/useTranslation';
import ModelType from '../../../Model/ModelType';
import './DialPanel.scss';
import T from '../../../../localization/LocalizationKeys';
import Icons, { Size } from '../../../Common/Icons';
import Highcharts from 'highcharts';
import _ from 'lodash';
import useInterval from '../../../../utils/hooks/useInterval';
import useMeasurementSystem from 'components/Value/useMeasurementSystem';

const DEFAULT_REFRESH_INTERVAL = 5; // 5 minutes
const MIN_REFRESH_INTERVAL = 5; // 5 minutes

// TODO fix the timezone offset so that it is calculated everytime instead as a option initially
const fixedOptions = {
  chart: {
    type: 'solidgauge'
  },
  exporting: {
    enabled: false
  },

  tooltip: {
    enabled: false
  },
  credits: {
    enabled: false
  }
};

const initialOptions = {
  title: {
    text: ''
  },

  pane: {
    center: ['50%', '85%'],
    size: '140%',
    startAngle: -90,
    endAngle: 90,
    background: {
      backgroundColor:
        Highcharts.defaultOptions.legend.backgroundColor || '#EEE',
      innerRadius: '60%',
      outerRadius: '100%',
      shape: 'arc'
    }
  },
  yAxis: {
    stops: [
      [0.1, '#55BF3B'], // green
      [0.5, '#DDDF0D'], // yellow
      [0.9, '#DF5353'] // red
    ],
    lineWidth: 0,
    tickWidth: 0,
    minorTickInterval: null,
    tickAmount: 2,
    title: {
      y: -70
    },
    labels: {
      y: 16
    },
    min: 0,
    max: 100
  },
  plotOptions: {
    solidgauge: {
      dataLabels: {
        y: 5,
        borderWidth: 0,
        useHTML: true
      }
    }
  }
};

const DialPanel = ({ data, api }) => {

  const { refreshInterval, hasError, isLoading } = data.signalValues;

  useInterval(api.reload, Math.max(MIN_REFRESH_INTERVAL, (refreshInterval ?? DEFAULT_REFRESH_INTERVAL) * 60000));

  const T = useTranslation();
  const loadingDescription = hasError ? T.common.error.generic : T.common.loading;
  const { convertToUserValue } = useMeasurementSystem();
  const options = useMemo(() => {
    const newOptions = _.cloneDeep(data.options ?? initialOptions);
    const max = data.signalValues.signals[0] ? convertToUserValue({ value: newOptions.yAxis.max, schemaUnit: data.signalValues.signals[0] }) : newOptions.yAxis.max;
    const min = data.signalValues.signals[0] ? convertToUserValue({ value: newOptions.yAxis.min, schemaUnit: data.signalValues.signals[0] }) : newOptions.yAxis.min;
    newOptions.yAxis.max = max;
    newOptions.yAxis.min = min;
    return _.merge({}, newOptions, fixedOptions, { time: { timezoneOffset: new Date().getTimezoneOffset() } });
  }, [convertToUserValue, data.options, data.signalValues.signals]);
  return (
    <div className={'dial-panel-container'}>
      { (isLoading || hasError) && <Flex alignItems={{ default: 'alignItemsCenter' }}>
        <Spinner size="md" />
        <span>{ loadingDescription }</span>
      </Flex> }
      <DialGraphComponent
        graphDataPackage={(data.signalValues?.modified) ? data.signalValues?.modified : data.signalValues.data}
        options={options}
        reflowTrigger={api.size.width}
      />
    </div>
  );
};

DialPanel.models = [
  {
    path: 'refreshInterval',
    type: ModelType.NUMBER,
    label: T.common.refreshInterval,
    placeholder: _.toString(DEFAULT_REFRESH_INTERVAL)
  },
  {
    type: ModelType.JSON_EDIT_DIALOG,
    path: 'options',
    label: T.dashboard.editor.inputs.json.options,
    properties: {
      height: '380px',
      width: '90%',
      initialValue: initialOptions
    }
  }
];


DialPanel.info = {
  label: 'Dial Classic',
  icon: <Icons.Dial size={Size.LARGE} />,
  help: 'panels/dial'
};

DialPanel.properties = {
  signalValues: {
    type: 'lastValue'
  }
};

export default React.memo(DialPanel);
